import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaShieldAlt, FaLeaf, FaArrowRight, FaClock, FaUsers, FaLightbulb, FaBookOpen, FaHandPaper } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import skinProblemsImage from '../../components/assets/Staff/SKIN PROBLEMS1.jpg';

const SkinProblems = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const supportRef = useRef(null);
  const researchRef = useRef(null);
  const summaryRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'support': supportRef,
      'research': researchRef,
      'summary': summaryRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'support', ref: supportRef },
        { id: 'research', ref: researchRef },
        { id: 'summary', ref: summaryRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Holistic Perspective', icon: FaHeart },
    { id: 'support', label: 'Skin Support', icon: FaLeaf },
    { id: 'research', label: 'Research & Evidence', icon: FaBookOpen },
    { id: 'summary', label: 'In Summary', icon: FaLightbulb }
  ];

  const overviewFeatures = [
    {
      icon: FaHandPaper,
      title: "Many Contributing Factors",
      description: "Skin conditions can be linked to genetics, immune reactivity, environmental exposure, hormonal balance, emotional stress or lifestyle"
    },
    {
      icon: FaShieldAlt,
      title: "Complementary Support",
      description: "This frequency-based method does not diagnose skin conditions and does not replace dermatological care"
    },
    {
      icon: FaLeaf,
      title: "Balance & Comfort",
      description: "Applied to support balance and comfort, particularly in recurring sensitivities or immune-related skin reactions"
    }
  ];

  const supportReasons = [
    "experience recurring or unexplained skin reactions",
    "notice links between stress, sensitivities and flare-ups",
    "want additional support alongside conventional skincare or dermatological care",
    "prefer non-invasive, personalised wellbeing approaches"
  ];

  const additionalSources = [
    { title: "The Open Epidemiology Journal (2020)", url: "https://benthamopen.com/FULLTEXT/TOEPIJ-8-1" },
    { title: "ScienceDirect – clinical and observational research overview", url: "https://www.sciencedirect.com/science/article/abs/pii/S1876382009001486" }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Bioresonance for Skin Problems" description="Bioresonance therapy for skin conditions at Bioresonance Leicester – complementary support for balance and comfort. Book at 108 New Walk." path="/therapy/bioresonance-therapy/skin-problems" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
          <ol className="flex items-center space-x-2 text-sm text-dark-grey">
            <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy/bioresonance-therapy" className="hover:text-gold transition-colors">Bioresonance Therapy</Link></li>
            <li className="text-gold">/</li>
            <li className="text-gold font-semibold">Skin Problems</li>
          </ol>
        </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHeart className="mr-2" />
                Bioresonance Sub-Treatment
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                SKIN PROBLEMS
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed font-semibold">
                Supporting skin balance and comfort in a complementary way
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Skin concerns are among the most visible and emotionally challenging issues people experience. Redness, itching, irritation, breakouts or recurring flare-ups can affect not only physical comfort but also confidence, social interactions and overall wellbeing.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                For many individuals, skin problems persist despite lifestyle changes, topical products or long-term management strategies. This often leads people to explore additional, non-invasive ways of supporting the body alongside conventional dermatological care.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance is one such option, applied in practice as a frequency-based approach.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={skinProblemsImage}
                      alt="Skin problems support"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <button
                  type="button"
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-gold text-black shadow-lg transform scale-105'
                      : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                  }`}
                  aria-current={activeSection === section.id ? 'location' : undefined}
                >
                  <SectionIcon className="mr-2 text-sm" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>
      </section>

      {/* Understanding skin problems from a holistic perspective */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Understanding skin problems from a holistic perspective
              </h2>
              <p className="text-lg text-gray-600">
                Skin conditions can have many contributing factors. While some are linked to genetics, others may be influenced by immune reactivity, environmental exposure, hormonal balance, emotional stress or lifestyle-related factors.
              </p>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Commonly reported skin concerns include acne, eczema, hives, cold sores and various inflammatory or sensitivity-based reactions. Some conditions are temporary, while others may recur over long periods of time.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                  >
                    <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-2xl text-gold" />
                    </div>
                    <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                From a holistic perspective, the skin is often viewed as closely connected to immune balance, nervous system regulation and the overall stress load placed on the body. Rather than focusing on symptoms alone, complementary approaches aim to support the individual as a whole.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Within complementary wellness settings, this frequency-based method does not diagnose skin conditions and does not replace dermatological care. Instead, it is applied to support balance and comfort, particularly in individuals who experience recurring sensitivities or immune-related skin reactions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skin support using frequency-based methods */}
      <section ref={supportRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              Skin support using frequency-based methods
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              This approach has a long-standing association with skin-related concerns, especially in cases where allergies or sensitivities may play a role. As many skin reactions involve immune system activity, some individuals choose this form of support as a gentle, non-invasive complementary option.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Sessions are personalised and informed by individual response patterns. The method does not involve medication, topical substances or physical manipulation.
            </p>
            
            <p className="text-gray-600 mb-4 leading-relaxed font-semibold">
              People often seek this type of support for skin concerns when they:
            </p>
            <ul className="space-y-3 text-gray-600 leading-relaxed list-none mb-6">
              {supportReasons.map((reason, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-gold mr-3 mt-1">•</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
            
            <p className="text-gray-600 leading-relaxed italic">
              Responses vary between individuals, and no specific outcome can be guaranteed.
            </p>
          </div>
        </div>
      </section>

      {/* Selected clinical observations and research */}
      <section ref={researchRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Selected clinical observations and research
              </h2>
              <p className="text-lg text-gray-600">
                One frequently referenced clinical observation examined the use of the BICOM® bioresonance device in individuals with allergic skin conditions.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20 mb-8">
              <h3 className="text-2xl font-bold text-dark-grey mb-4">Study title:</h3>
              <p className="text-lg text-gray-600 mb-6 font-medium">
                Clinical Observation of 79 Cases of Allergic Skin Diseases Treated with BICOM2000 Bio-Resonance Device
              </p>
              
              <h4 className="text-lg font-semibold text-dark-grey mb-2">Author:</h4>
              <p className="text-gray-600 mb-6">
                Yang Jinzhi, Department of Allergy, Jinan Children's Hospital
              </p>

              <h4 className="text-lg font-semibold text-dark-grey mb-2">Study overview:</h4>
              <p className="text-gray-600 mb-4">
                <strong>Participants:</strong> 79 individuals with allergic skin conditions
              </p>
              <p className="text-gray-600 mb-6">
                <strong>Method:</strong> Allergy assessment and desensitisation using the BICOM2000 device
              </p>

              <h4 className="text-lg font-semibold text-dark-grey mb-2">Reported outcomes:</h4>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>Total reported effectiveness rate: 89.9%</li>
                <li>Reported complete symptom resolution: 74.7%</li>
                <li>No adverse side effects were observed</li>
                <li>The method was reported to be particularly well tolerated, including in cases of infant eczema.</li>
              </ul>

              <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-lg p-6 border border-gold/20 mb-6">
                <p className="text-gray-600 leading-relaxed">
                  The study describes the concept that substances, including allergens, carry specific electromagnetic signatures. When sensitivity develops, these signals may become altered. Bioresonance aims to reflect modified signals back to the body in order to support natural regulatory processes.
                </p>
              </div>

              <p className="text-gray-600 leading-relaxed mb-6">
                This frequency-based method is used as a modern, non-invasive form of complementary support in addressing skin-related concerns. Its application is based on clinical observations and individual response, which is why it is often chosen by people seeking personalised solutions.
              </p>

              <h4 className="text-lg font-semibold text-dark-grey mb-2">Full study publication (PDF):</h4>
              <a 
                href="https://bicom.se/wp-content/uploads/2017/03/Clinical-Observation-of-79-Cases.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue hover:text-gold transition-colors duration-200 break-all inline-flex items-center"
              >
                https://bicom.se/wp-content/uploads/2017/03/Clinical-Observation-of-79-Cases.pdf
                <FaArrowRight className="ml-2 text-sm flex-shrink-0" />
              </a>
            </div>

            <div className="mb-8">
              <h4 className="text-lg font-semibold text-dark-grey mb-4">Additional research and background sources include:</h4>
              <ul className="space-y-3">
                {additionalSources.map((source, index) => (
                  <li key={index}>
                    <a 
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue hover:text-gold transition-colors duration-200"
                    >
                      {source.title}
                    </a>
                    <br />
                    <span className="text-sm text-gray-500 break-all">{source.url}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* In summary */}
      <section ref={summaryRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              In summary
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              Bioresonance, applied as a frequency-based method using the BICOM® system, is a non-invasive complementary option that some individuals choose to explore in relation to skin comfort and balance. It takes a holistic view of the individual and is valued by many for its gentle nature and personalised focus.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Complementary Support for Skin Comfort?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Bioresonance, applied as a frequency-based method using the BICOM® system, is a non-invasive complementary option that some individuals choose to explore in relation to skin comfort and balance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Consultation
            </a>
          <Link 
            to="/therapy/bioresonance-therapy"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
          >
              Back to Bioresonance Therapy
          </Link>
        </div>
      </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default SkinProblems;
